import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Props {
  onSuccess: () => void;
}

export default function AddFeeTemplateDialog({ onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [feeType, setFeeType] = useState("monthly");
  const [description, setDescription] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.nursery_id || !name || !amount) return;
    setLoading(true);

    const { error } = await supabase.from("fee_templates").insert({
      nursery_id: profile.nursery_id,
      name,
      amount: parseFloat(amount),
      fee_type: feeType,
      description: description || null,
    });

    setLoading(false);
    if (error) {
      toast.error("خطأ: " + error.message);
    } else {
      toast.success("تم إضافة قالب الرسوم");
      setOpen(false);
      setName("");
      setAmount("");
      setDescription("");
      onSuccess();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="w-4 h-4 ml-1" />
          قالب رسوم
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة قالب رسوم</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>اسم الرسوم *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="رسوم شهرية" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>المبلغ (ج.م) *</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required min={0} step={0.01} dir="ltr" />
            </div>
            <div>
              <Label>النوع</Label>
              <Select value={feeType} onValueChange={setFeeType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">شهرية</SelectItem>
                  <SelectItem value="additional">إضافية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>وصف</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="وصف اختياري" />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
